/**
 * VerseSport — Real Data Migration Policy (mandatory).
 *
 * Single source of truth that classifies every value shown in the app into
 * two categories, and exposes helpers so components can decide when a
 * simulated value is acceptable vs when official provider data is required.
 *
 * CATEGORY A — OFFICIAL FOOTBALL DATA
 *   Must come from API-Football whenever available. Never simulated during
 *   LIVE or FINISHED matches. During SCHEDULED (pre-match) state, no fake
 *   live statistics may be fabricated — components must render a pre-match
 *   placeholder instead.
 *
 * CATEGORY B — VERSESPORT COMMUNITY DATA
 *   Belongs to VerseSport itself (not football). May remain simulated
 *   until enough real users exist. Will gradually transition to real data.
 *
 * Modules that MUST NOT be modified by this policy:
 *   Share Engine, Prediction Engine, Nation Wars, Identity,
 *   Fan Support, Community modules, Gamification.
 */

export type MatchLifecycle = "SCHEDULED" | "LIVE" | "FINISHED";

/** Category A — official football data keys. */
export const OFFICIAL_FOOTBALL_KEYS = [
  "status", "kickoff", "clock", "score", "goals", "goal_scorers",
  "assists", "cards", "substitutions", "var", "penalties",
  "possession", "shots", "shots_on_target", "corners", "fouls",
  "offsides", "pass_accuracy", "team_stats", "player_stats",
  "official_lineups", "bench", "coach", "match_events", "timeline",
] as const;
export type OfficialFootballKey = (typeof OFFICIAL_FOOTBALL_KEYS)[number];

/** Category B — VerseSport community keys. Simulated is acceptable. */
export const COMMUNITY_KEYS = [
  "active_fans", "watching_now", "community_participation", "fan_support",
  "chat_activity", "community_messages", "trending_reactions",
  "prediction_participation", "community_polls", "nation_wars_activity",
  "xp", "badges", "missions", "challenges", "user_levels", "reputation",
  "community_rankings", "viral_activity", "social_counters",
] as const;
export type CommunityKey = (typeof COMMUNITY_KEYS)[number];

/**
 * True when a simulated/synthetic value for the given football key is
 * allowed to render. Only pre-match placeholders that are NOT live
 * statistics are permitted; live stats (possession, shots, xG, etc.) must
 * never be fabricated even before kickoff.
 */
const NEVER_FABRICATE_PRE_MATCH: ReadonlySet<OfficialFootballKey> = new Set([
  "possession", "shots", "shots_on_target", "cards", "fouls", "corners",
  "offsides", "pass_accuracy", "goals", "goal_scorers", "assists",
  "substitutions", "var", "penalties", "match_events", "timeline",
  "team_stats", "player_stats",
]);

export function canSimulateOfficial(
  key: OfficialFootballKey,
  lifecycle: MatchLifecycle,
): boolean {
  if (lifecycle === "LIVE" || lifecycle === "FINISHED") return false;
  return !NEVER_FABRICATE_PRE_MATCH.has(key);
}

export function isCommunityKey(key: string): key is CommunityKey {
  return (COMMUNITY_KEYS as readonly string[]).includes(key);
}

export function isOfficialFootballKey(key: string): key is OfficialFootballKey {
  return (OFFICIAL_FOOTBALL_KEYS as readonly string[]).includes(key);
}

/**
 * Guard used by live-stat renderers. Returns true when the component must
 * show a pre-match placeholder instead of a fabricated value.
 */
export function requiresPreMatchPlaceholder(
  key: OfficialFootballKey,
  lifecycle: MatchLifecycle,
  hasProviderValue: boolean,
): boolean {
  if (hasProviderValue) return false;
  return !canSimulateOfficial(key, lifecycle);
}